import { useEffect } from "react";
import type eruda from "eruda";
import { setUser } from "@sentry/react";
import { useLocation } from "@tanstack/react-router";
import type { CurrentUser } from "@/lib/client/user";
import { ensureWechatJssdkConfigured } from "@/lib/client/wechat-jssdk";
import { isWeChatBrowser } from "@/lib/utils/wechat";

const ERUDA_STORAGE_KEY = "debug:eruda-enabled";
const ERUDA_DISABLE_VALUES = new Set(["0", "false", "off"]);
let erudaBootstrapPromise: Promise<void> | null = null;

export const EARLY_ERUDA_BOOTSTRAP_SCRIPT = `
(() => {
  const STORAGE_KEY = ${JSON.stringify(ERUDA_STORAGE_KEY)};
  const DISABLE_VALUES = ["0", "false", "off"];
  const params = new URLSearchParams(window.location.search);
  const rawDev = params.get("dev");
  const shouldEnable =
    rawDev !== null
      ? !DISABLE_VALUES.includes(String(rawDev).toLowerCase())
      : window.localStorage.getItem(STORAGE_KEY) === "1";

  if (!shouldEnable || window.__ERUDA_EARLY_BUFFER__) {
    return;
  }

  const methods = ["log", "info", "warn", "error", "debug"];
  const originals = {};
  const state = {
    buffering: true,
    logs: [],
  };

  for (const method of methods) {
    originals[method] = console[method].bind(console);
    console[method] = (...args) => {
      if (state.buffering) {
        state.logs.push({ method, args });
      }

      return originals[method](...args);
    };
  }

  window.__ERUDA_EARLY_BUFFER__ = state;
  window.__ERUDA_EARLY_FLUSH__ = () => {
    if (!state.buffering) {
      return;
    }

    state.buffering = false;
    const logs = state.logs.splice(0);
    for (const entry of logs) {
      try {
        console[entry.method](...entry.args);
      } catch {}
    }
  };
})();
`;

export function RootUserEffects({ user }: { user: CurrentUser | null }) {
  useEffect(() => {
    setUser(
      user
        ? {
            ...user,
            email: user.email ?? undefined,
          }
        : null,
    );

    if (typeof window === "undefined" || !user) {
      return;
    }

    window.umami?.identify({
      ...user,
      email: undefined,
      password: undefined,
    });
  }, [user]);

  return null;
}

export function WechatJssdkBootstrap() {
  const locationHref = useLocation({ select: (state) => state.href });

  useEffect(() => {
    if (typeof window === "undefined" || !isWeChatBrowser()) {
      return;
    }

    const fullUrl = `${window.location.origin}${locationHref}`.split("#")[0] || "/";
    ensureWechatJssdkConfigured(fullUrl).catch((error) => {
      console.error("微信 JSSDK 初始化失败", error);
    });
  }, [locationHref]);

  return null;
}

export function ErudaBootstrap() {
  const locationSearch = useLocation({ select: (state) => state.searchStr });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const searchParams = new URLSearchParams(locationSearch);
    const rawDev = searchParams.get("dev");
    const shouldEnable = getErudaPreference(locationSearch);

    if (rawDev !== null) {
      if (shouldEnable) {
        localStorage.setItem(ERUDA_STORAGE_KEY, "1");
      } else {
        localStorage.removeItem(ERUDA_STORAGE_KEY);
      }
    }

    if (shouldEnable) {
      ensureErudaEnabled().catch((error) => {
        console.error("eruda 初始化失败", error);
      });
      return;
    }

    disableEruda().catch((error) => {
      console.error("eruda 关闭失败", error);
    });
  }, [locationSearch]);

  return null;
}

async function ensureErudaEnabled() {
  if (typeof window === "undefined") {
    return;
  }

  if (erudaBootstrapPromise) {
    return await erudaBootstrapPromise;
  }

  erudaBootstrapPromise = (async () => {
    const erudaModule = await import("eruda");
    const erudaInstance = erudaModule.default as typeof eruda;

    if (document.getElementById("eruda")) {
      (window as Window & { __ERUDA_EARLY_FLUSH__?: () => void }).__ERUDA_EARLY_FLUSH__?.();
      return;
    }

    erudaInstance.init();
    (window as Window & { __ERUDA_EARLY_FLUSH__?: () => void }).__ERUDA_EARLY_FLUSH__?.();
  })();

  return await erudaBootstrapPromise;
}

async function disableEruda() {
  if (typeof window === "undefined") {
    return;
  }

  const erudaInstance = (window as Window & { eruda?: typeof eruda }).eruda;
  erudaInstance?.destroy();
  erudaBootstrapPromise = null;
}

function getErudaPreference(search: string) {
  const searchParams = new URLSearchParams(search);
  const rawDev = searchParams.get("dev");
  if (rawDev !== null) {
    return !ERUDA_DISABLE_VALUES.has(rawDev.toLowerCase());
  }

  return localStorage.getItem(ERUDA_STORAGE_KEY) === "1";
}
